/**
 * 运行时索引：把生成出来的数据表变成可查询、可复用的结构。
 *
 * 所有索引都是**懒建**的。冷启动时协议栈可能只需要一两条消息的定义，
 * 却要为 326 条消息、222 个枚举和几千个枚举条目付出建索引的开销，没有必要。
 */
import { ENUMS } from './generated/enums.generated';
import { MESSAGES } from './generated/messages.generated';
import { PROTOCOL_SOURCE } from './generated/upstream.generated';
import {
  FIELD_TYPE_SIZES,
  type EnumDefinition,
  type EnumEntryDefinition,
  type FieldDefinition,
  type MessageDefinition,
} from './types';

export { PROTOCOL_SOURCE };

export function allMessages(): readonly MessageDefinition[] {
  return MESSAGES;
}

export function allEnums(): readonly EnumDefinition[] {
  return ENUMS;
}

let messageByIdIndex: Map<number, MessageDefinition> | null = null;
let messageByNameIndex: Map<string, MessageDefinition> | null = null;

function messageByIdMap(): Map<number, MessageDefinition> {
  if (messageByIdIndex === null) {
    messageByIdIndex = new Map(MESSAGES.map((message) => [message.id, message]));
  }
  return messageByIdIndex;
}

function messageByNameMap(): Map<string, MessageDefinition> {
  if (messageByNameIndex === null) {
    messageByNameIndex = new Map(MESSAGES.map((message) => [message.name, message]));
  }
  return messageByNameIndex;
}

export function messageById(id: number): MessageDefinition | undefined {
  return messageByIdMap().get(id);
}

export function messageByName(name: string): MessageDefinition | undefined {
  return messageByNameMap().get(name);
}

/**
 * 按名字取消息 ID。
 *
 * 比硬编码数字可读得多：`messageIdOf('AUTOPILOT_VERSION')` 不会因为记错
 * "178 还是 148" 而静默地请求到另一条消息。
 */
export function messageIdOf(name: string): number | undefined {
  return messageByNameMap().get(name)?.id;
}

let enumByNameIndex: Map<string, EnumDefinition> | null = null;
let enumEntryIndex: Map<string, EnumEntryDefinition> | null = null;

function enumByNameMap(): Map<string, EnumDefinition> {
  if (enumByNameIndex === null) {
    enumByNameIndex = new Map(ENUMS.map((enumDef) => [enumDef.name, enumDef]));
  }
  return enumByNameIndex;
}

function enumEntryMap(): Map<string, EnumEntryDefinition> {
  if (enumEntryIndex === null) {
    enumEntryIndex = new Map();
    for (const enumDef of ENUMS) {
      for (const entry of enumDef.entries) {
        enumEntryIndex.set(`${enumDef.name}.${entry.name}`, entry);
      }
    }
  }
  return enumEntryIndex;
}

export function enumByName(name: string): EnumDefinition | undefined {
  return enumByNameMap().get(name);
}

export function enumEntry(enumName: string, entryName: string): EnumEntryDefinition | undefined {
  return enumEntryMap().get(`${enumName}.${entryName}`);
}

/**
 * 取枚举条目的数值。
 *
 * 返回 `null` 而不是 `undefined` 是有意的：调用方几乎总是要把它放进
 * "要么有值要么明确没有"的语义里，`null` 与 `undefined` 混用会逼出
 * 到处都是的 `??` 兜底，掩盖真正的问题。
 */
export function enumValue(enumName: string, entryName: string): number | null {
  const entry = enumEntry(enumName, entryName);
  return entry === undefined ? null : entry.value;
}

/** 反向查名字：数值 → 条目。用于把 `custom_mode` 之类的裸数字翻译成人能读的名字。 */
export function enumEntryByValue(enumName: string, value: number): EnumEntryDefinition | undefined {
  return enumByNameMap()
    .get(enumName)
    ?.entries.find((entry) => entry.value === value);
}

export function fieldByteLength(field: FieldDefinition): number {
  const elementSize = FIELD_TYPE_SIZES[field.type];
  return elementSize * (field.arrayLength === 0 ? 1 : field.arrayLength);
}

/**
 * 编译后的字段表。
 *
 * 解析每帧时都要算一遍"第 i 个字段从第几个字节开始"，而字段布局只与消息定义有关、
 * 与帧无关。编译一次缓存起来，热路径上就只剩算术。
 */
export interface CompiledField {
  readonly definition: FieldDefinition;
  /** 在 payload 中的字节偏移 */
  readonly offset: number;
  readonly elementSize: number;
  /** 标量是 1，数组是元素个数 */
  readonly elementCount: number;
  readonly byteLength: number;
  /** 在消息 `fields` 数组里的下标 —— 写回数值时按它定位 */
  readonly index: number;
}

export interface CompiledMessage {
  readonly definition: MessageDefinition;
  readonly fields: readonly CompiledField[];
  /** 不含扩展字段的线上长度，即 MAVLink 1 的 payload 长度、MAVLink 2 的 `_MIN_LEN` */
  readonly baseLength: number;
  /** 含扩展字段的完整长度 */
  readonly wireLength: number;
}

const compiledCache = new Map<number, CompiledMessage>();

function compile(message: MessageDefinition): CompiledMessage {
  const fields: CompiledField[] = [];
  let offset = 0;
  let baseLength = 0;

  message.fields.forEach((definition, index) => {
    const elementSize = FIELD_TYPE_SIZES[definition.type];
    const elementCount = definition.arrayLength === 0 ? 1 : definition.arrayLength;
    const byteLength = elementSize * elementCount;
    fields.push({ definition, offset, elementSize, elementCount, byteLength, index });
    if (!definition.extension) baseLength = offset + byteLength;
    offset += byteLength;
  });

  return { definition: message, fields, baseLength, wireLength: offset };
}

export function compileMessage(message: MessageDefinition): CompiledMessage {
  const cached = compiledCache.get(message.id);
  if (cached !== undefined && cached.definition === message) return cached;
  const compiled = compile(message);
  compiledCache.set(message.id, compiled);
  return compiled;
}

export function compiledById(id: number): CompiledMessage | undefined {
  const message = messageById(id);
  return message === undefined ? undefined : compileMessage(message);
}

export function compiledByName(name: string): CompiledMessage | undefined {
  const message = messageByName(name);
  return message === undefined ? undefined : compileMessage(message);
}
